import { faFolderOpen } from '@fortawesome/free-solid-svg-icons';
import { useApi } from '@hooks/useApi';
import useModalManager from '@hooks/useModalManager';
import { EmptyState } from '@ui';
import Loader from 'components/Loader';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR, { useSWRConfig } from 'swr';
import { useConfirmedCallback } from '~/hooks/useConfirmedCallback';
import ClubMemberFileEditForm from './ClubMemberFileEditForm';
import FileUploadZone from './FileUploadZone';
import MemberFileRow from './MemberFileRow';
import { MemberFile } from './types';
import { useFileUploads } from './useFileUploads';

interface Props {
    memberId: number;
}

export const memberFilesKey = (memberId: number) => `club-member/${memberId}/files`;

/**
 * The club's file folder on a member: upload, rename, set validity and share with the member.
 * Saves right away, so it also lives outside the member form's submit.
 */
export const ClubMemberFileManager = ({ memberId }: Props) => {
    const { t } = useTranslation('member-management');
    const api = useApi();
    const { addModal, removeModal } = useModalManager();
    const { data, isLoading, mutate } = useSWR<{ data: MemberFile[]; meta: { can_manage: boolean } }>(
        memberId ? memberFilesKey(memberId) : null
    );
    const files = data?.data ?? [];
    const canManage = !!data?.meta?.can_manage;

    const { mutate: globalMutate } = useSWRConfig();

    const refresh = useCallback(() => {
        void mutate();
        void globalMutate((key) => typeof key === 'string' && key.endsWith('/dashboard/expiring-files'));
    }, [mutate, globalMutate]);
    const { pending, upload, dismiss } = useFileUploads(memberFilesKey(memberId), refresh);

    const openFile = (file: MemberFile) =>
        api({
            url: `${memberFilesKey(memberId)}/${file.id}/url`,
            onSuccess: ({ url }: { url: string }) => window.open(url, '_blank', 'noopener')
        });

    const editFile = (file: MemberFile) =>
        addModal({
            title: t('clubMember.files.actions.edit'),
            content: (id) => (
                <ClubMemberFileEditForm
                    memberId={memberId}
                    file={file}
                    onSaved={() => {
                        removeModal(id);
                        refresh();
                    }}
                />
            ),
            show: true,
            size: 40
        });

    const deleteFile = useConfirmedCallback(
        (file: MemberFile) =>
            api({
                url: `${memberFilesKey(memberId)}/${file.id}`,
                method: 'DELETE',
                onSuccess: refresh
            }),
        {
            destructive: true,
            message: (file: MemberFile) => t('clubMember.files.actions.deleteConfirm', { title: file.display_title })
        }
    );

    return (
        <div className="tw-flex tw-flex-col tw-gap-3">
            {canManage && (
                <FileUploadZone
                    name={`member_files_${memberId}`}
                    pending={pending}
                    onFiles={upload}
                    onDismiss={dismiss}
                />
            )}
            <Loader isLoading={isLoading}>
                {files.length === 0 ? (
                    <EmptyState
                        size="sm"
                        icon={faFolderOpen}
                        title={t('clubMember.files.empty')}
                        description={canManage ? t('clubMember.files.uploadHint') : undefined}
                    />
                ) : (
                    <div>
                        {files.map((file) => (
                            <MemberFileRow
                                key={file.id}
                                file={file}
                                showClubDetails
                                onOpen={openFile}
                                onEdit={canManage ? editFile : undefined}
                                onDelete={canManage ? deleteFile : undefined}
                            />
                        ))}
                    </div>
                )}
            </Loader>
        </div>
    );
};

export default ClubMemberFileManager;
